import React, { useState } from 'react';
import { motion } from 'framer-motion';

const OffersList: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'all' | 'featured' | 'expiring'>('all');
  
  // Données mockées des offres
  const offers = [
    {
      id: 1,
      title: "2 cafés pour le prix d'un",
      shop: "Café des Artistes",
      points: 150,
      expiry: "3 jours",
      image: "https://images.unsplash.com/photo-1497515114629-f71d768fd07c?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60",
      featured: true,
      expiring: true,
      category: "restaurant"
    },
    {
      id: 2,
      title: "10% sur tous les livres",
      shop: "Librairie Page & Plume",
      points: 200,
      expiry: "7 jours",
      image: "https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60",
      featured: true,
      expiring: false,
      category: "shopping"
    },
    {
      id: 3,
      title: "Une pâtisserie offerte",
      shop: "Boulangerie Tradition",
      points: 100,
      expiry: "5 jours",
      image: "https://images.unsplash.com/photo-1517433367423-c7e5b0f35086?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60",
      featured: false,
      expiring: true,
      category: "alimentation"
    },
    {
      id: 4,
      title: "20€ de réduction",
      shop: "Mode Citadine",
      points: 300,
      expiry: "14 jours",
      image: "https://images.unsplash.com/photo-1551488831-00ddcb6c6bd3?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=60",
      featured: true,
      expiring: false,
      category: "shopping"
    }
  ];
  
  const filteredOffers = offers.filter(offer => {
    if (activeTab === 'all') return true;
    if (activeTab === 'featured') return offer.featured;
    if (activeTab === 'expiring') return offer.expiring;
    return true;
  });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'all' 
                ? 'bg-white text-[#007BFF] shadow-md' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Toutes
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('featured')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'featured' 
                ? 'bg-white text-[#007BFF] shadow-md' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            En vedette
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('expiring')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              activeTab === 'expiring' 
                ? 'bg-white text-[#007BFF] shadow-md' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            Bientôt expirées
          </motion.button>
        </div>
      </div>
      
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {filteredOffers.map((offer) => (
          <motion.div
            key={offer.id}
            variants={itemVariants}
            whileHover={{ 
              y: -10, 
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)" 
            }}
            className="bg-white/10 backdrop-blur-md rounded-xl overflow-hidden border border-white/20 shadow-lg"
          >
            <div className="relative h-48 overflow-hidden">
              <img 
                src={offer.image} 
                alt={offer.title} 
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
              />
              <div className="absolute top-3 right-3 bg-[#7B1FA2] text-white text-xs font-bold px-2 py-1 rounded-md">
                {offer.points} pts
              </div>
              {offer.expiring && (
                <div className="absolute bottom-3 left-3 bg-[#FF5252]/90 text-white text-xs font-bold px-2 py-1 rounded-md flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Expire dans {offer.expiry}
                </div>
              )}
            </div>
            
            <div className="p-5">
              <div className="flex items-center text-xs text-white/80 mb-2">
                <span className="capitalize">{offer.category}</span>
                <span className="mx-2">•</span>
                <span>{offer.shop}</span>
              </div>
              
              <h3 className="font-bold text-lg mb-2">{offer.title}</h3>
              
              <div className="flex justify-between items-center mt-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-3 py-1.5 bg-gradient-to-r from-[#007BFF] to-[#40E0D0] text-white text-sm font-medium rounded-lg shadow-md"
                >
                  Échanger
                </motion.button>
                
                <button className="text-white/80 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
      
      <div className="flex justify-center mt-10">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 bg-white/20 text-white rounded-lg font-medium shadow-md hover:bg-white/30 transition-colors"
        >
          Voir toutes les offres
        </motion.button>
      </div>
    </div>
  );
};

export default OffersList;
